import { useFormikContext } from 'formik';
import {
  CalendarFold,
  CirclePlus,
  Headset,
  HeartPulse,
  LayoutGrid,
  UsersRound,
} from 'lucide-react';

import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';

import type { AgentCreateFormValues } from '@/schemas/agent.schema';

import type { AgentTemplate } from '@/types/agent.type';

const TEMPLATE_ICON_MAP: Record<string, React.ElementType> = {
  'Customer Support Agent': Headset,
  'Appointment Scheduling Agent': CalendarFold,
  'Lead Qualification Agent': UsersRound,
  'Care Coordinator Agent': HeartPulse,
};

type TemplateType = 'blank' | string;

type Props = {
  templates: AgentTemplate[];
  isLoading: boolean;
};

export const CreateAgentFromTemplate = ({ templates, isLoading }: Props) => {
  const { values, setFieldValue } = useFormikContext<AgentCreateFormValues>();

  return (
    <RadioGroup
      value={values.template_id || ''}
      onValueChange={value => setFieldValue('template_id', value as TemplateType)}
      className="flex flex-col gap-6 w-full"
    >
      {/* Start from blank option */}
      <label
        htmlFor="blank"
        className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
          values.template_id === 'blank'
            ? 'border-primary bg-primary-light'
            : 'border-border hover:border-muted-foreground/50'
        }`}
      >
        <div className="flex items-center py-0.5">
          <CirclePlus className="h-5 w-5 opacity-80" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <p className="font-semibold text-sm leading-5 text-foreground">Start from blank</p>
          <p className="font-normal text-sm leading-5 text-muted-foreground">
            Start with a clean slate. This minimal template gives you the freedom to design and
            build your ideal agent.
          </p>
        </div>
        <RadioGroupItem value="blank" id="blank" className="sr-only" />
      </label>

      {/* Select template section */}
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center w-full">
          <p className="font-semibold text-base leading-6 text-foreground">Select template</p>
        </div>
        <div className="flex items-center w-full">
          <p className="font-normal text-sm leading-5 text-muted-foreground">
            Get started with pre-configured templates
          </p>
        </div>
      </div>

      {/* Template grid */}
      <div className="flex flex-wrap gap-4 w-full">
        {isLoading
          ? Array.from({ length: 6 }, (_, i) => (
              <Skeleton
                key={`skeleton-${i}`}
                className="rounded-xl w-[calc(33.333%-11px)] h-[104px]"
              />
            ))
          : templates.map(template => {
              const Icon = TEMPLATE_ICON_MAP[template.name] ?? LayoutGrid;
              return (
                <label
                  key={`${template.id}-${template.name}`}
                  htmlFor={template.id}
                  className={`flex flex-col items-start gap-2 p-4 border rounded-xl cursor-pointer transition-all w-[calc(33.333%-11px)] ${
                    values.template_id === template.id
                      ? 'border-primary border-[1.5px] bg-primary-light'
                      : 'border-border hover:border-muted-foreground/50'
                  }`}
                >
                  <Icon className="h-5 w-5 opacity-80" />
                  <p className="font-semibold text-sm leading-5 text-foreground">{template.name}</p>
                  {template.description && (
                    <p className="font-normal text-xs leading-4 text-muted-foreground line-clamp-2">
                      {template.description}
                    </p>
                  )}
                  <RadioGroupItem value={template.id} id={template.id} className="sr-only" />
                </label>
              );
            })}
      </div>
    </RadioGroup>
  );
};
